import type { JSX } from "react";
import { Button, Flex, Text } from "@aws-amplify/ui-react";

import { ToolbarActionIcon } from "./toolbar";

/** The most files a folder lists; the file view sorts all of them and pages no further. */
export const LISTING_PAGE_SIZE = 10_000;

const PREVIOUS_LABEL = "Previous";
const NEXT_LABEL = "Next";
const PAGINATION_BUTTON_CLASS =
  "evido-listing__toolbar-action evido-listing__toolbar-action--labelled";

type ListingPaginationProperties = {
  /** Names the rows in the range label, such as `Files` or `Folders`. */
  readonly itemLabel: string;
  readonly page: number;
  /** Rows on the current page; omit where a filter makes a range misleading. */
  readonly pageItemCount?: number;
  readonly hasNextPage: boolean;
  readonly highestPageVisited: number;
  readonly onPaginate: (page: number) => void;
};

function pageRangeLabel(
  itemLabel: string,
  page: number,
  pageItemCount: number,
): string {
  const firstItem = (page - 1) * LISTING_PAGE_SIZE + 1;
  const lastItem = firstItem + pageItemCount - 1;
  return `${itemLabel} ${String(firstItem)}–${String(lastItem)}`;
}

/**
 * The pager every listing shares.
 *
 * S3 lists a folder one page at a time, so the number of pages is never known
 * up front: a live Next button beside a range that moves is what tells the
 * reader more files are waiting. A listing that fits on one page shows nothing.
 */
export function ListingPagination({
  itemLabel,
  page,
  pageItemCount = 0,
  hasNextPage,
  highestPageVisited,
  onPaginate,
}: ListingPaginationProperties): JSX.Element | null {
  const hasPreviousPage = page > 1;
  const hasPageAhead = hasNextPage || page < highestPageVisited;
  if (!hasPreviousPage && !hasPageAhead) {
    return null;
  }

  const rangeLabel =
    pageItemCount > 0
      ? pageRangeLabel(itemLabel, page, pageItemCount)
      : undefined;

  return (
    <Flex alignItems="center" gap="0.5rem">
      {rangeLabel ? (
        <Text color="font.secondary" fontSize="small">
          {rangeLabel}
        </Text>
      ) : null}
      <Button
        className={PAGINATION_BUTTON_CLASS}
        isDisabled={!hasPreviousPage}
        onClick={() => {
          onPaginate(page - 1);
        }}
      >
        <ToolbarActionIcon name="chevron-left" />
        {PREVIOUS_LABEL}
      </Button>
      <Button
        className={PAGINATION_BUTTON_CLASS}
        isDisabled={!hasPageAhead}
        onClick={() => {
          onPaginate(page + 1);
        }}
      >
        {NEXT_LABEL}
        <ToolbarActionIcon name="chevron-right" />
      </Button>
    </Flex>
  );
}
